import { useTransform, type MotionValue } from 'framer-motion';
import type { RefObject } from 'react';
import type { measureChatTeaser3Layout } from './chatteaser3Layout';

type ChatTeaser3LayoutMetrics = ReturnType<typeof measureChatTeaser3Layout>;

export interface ChatTeaser3ScrollMotion {
  expandProgress: MotionValue<number>;
  messageScroll: MotionValue<number>;
  restHeight: MotionValue<number>;
  soloScrollOpacity: MotionValue<number>;
  unitedTop: MotionValue<number>;
}

export function getIntroRestHeight(layout: ChatTeaser3LayoutMetrics): number {
  return Math.max(0, layout.wrapperHeight - layout.soloBottom);
}

export function useChatteaser3ScrollMotion({
  layoutRef,
  layoutVersion,
  scrollY,
}: {
  layoutRef: RefObject<ChatTeaser3LayoutMetrics>;
  layoutVersion: MotionValue<number>;
  scrollY: MotionValue<number>;
}): ChatTeaser3ScrollMotion {
  const expandProgress = useTransform([scrollY, layoutVersion], ([amount]) => {
    const { soloBottom } = layoutRef.current;
    return soloBottom > 0 ? Math.min(1, (amount as number) / soloBottom) : 1;
  });

  const unitedTop = useTransform([expandProgress, layoutVersion], ([progress]) => {
    return layoutRef.current.soloBottom * (1 - (progress as number));
  });

  const messageScroll = useTransform([scrollY, layoutVersion], ([amount]) => {
    return Math.max(0, (amount as number) - layoutRef.current.soloBottom);
  });

  const restHeight = useTransform([unitedTop, layoutVersion], ([top]) => {
    return Math.max(0, layoutRef.current.wrapperHeight - (top as number));
  });

  const soloScrollOpacity = useTransform(expandProgress, (progress) => 1 - progress);

  return {
    expandProgress,
    messageScroll,
    restHeight,
    soloScrollOpacity,
    unitedTop,
  };
}
